import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { LookUpService } from '../../services/lookupservice';
import { ToteTransactionService } from '../../services/tote-transaction-service';
import { ToteTransactionInputData, ToteTransactionRecord } from '../../interfaces/tote-transaction-record';
import { Subject, takeUntil } from 'rxjs';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { RegionsInfo } from '../../interfaces/regions';
import { GetMarketGeneratorsResponse } from '../../interfaces/generatorsmodel';
import { FeedstockService } from '../../services/feedstockservice';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../shared/components/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AddEditToteTransactionComponent } from './add-edit-tote-transaction/add-edit-tote-transaction.component';
import { MatMenuModule } from '@angular/material/menu';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { LoadScheduleControlFlags, ScreenName } from '../../interfaces/securityPermissions';
import { dateRangeCompleteValidator, strictDateValidator } from '../shared/commonutils/helper';
import { AlertDialogComponent } from '../shared/components/alert-dialog/alert-dialog.component';
import { RoleService } from '../../services/role.service';

@Component({
  selector: 'app-tote-transaction',
  imports: [CommonModule, MatTableModule, MatCheckboxModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatSelectModule, MatInputModule, MatCardModule,
    MatSortModule, ReactiveFormsModule, MatDatepickerModule, MatMenuModule],
  templateUrl: './tote-transaction.component.html',
  styleUrl: './tote-transaction.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToteTransactionComponent implements OnInit {
  // Injected services
  permissions = inject(RolePermissionsService);
  private _dialog = inject(MatDialog)

  markets = signal<RegionsInfo[]>([]);
  selectedMarketId = signal<number>(-1);
  marketQuery = signal<string>('');
  generatorQuery=signal<string>('');
  @ViewChild('marketFilterInput') marketFilterInput?: ElementRef<HTMLInputElement>;
  generators=signal<GetMarketGeneratorsResponse[]>([]);
  selectedGeneratorId: number | null = null;

  private destroy$ = new Subject<void>();
  toteTransactions = new MatTableDataSource<any>;
  @ViewChild(MatSort) sort!: MatSort;
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  range: FormGroup = new FormGroup({
    start: new FormControl<Date | null>(null, [strictDateValidator]),
    end: new FormControl<Date | null>(null, [strictDateValidator]),
  }, {validators: dateRangeCompleteValidator});

  displayedColumns = [
    'actions', 'id','customerName', 'streamName', 'transactionType', 'transactionDate',
    'notes', 'transactionStatus', 'fee'
  ];

  ngOnInit(): void {
    
  }
  constructor(private _lookupService: LookUpService,
    private _toteTxService: ToteTransactionService,
    private _fsService: FeedstockService,
    private _cdr: ChangeDetectorRef, private dialog: MatDialog, private rolesService: RoleService) {

    //Intitilize date range
    this.initializeDefaultFilters();

    //Load the Markets
    this.loadMarkets();

    this.getToteTransactions();

    //When market changes: reset site, load sites, and clear pending
    effect(() => {
      const marketId = this.selectedMarketId();
      this.loadGenerators(marketId);
    });
  }

  private initializeDefaultFilters(): void {
    const today = new Date();
    const starttDateOfLastMonth = this.getStartOfLastMonth();
    this.range = new FormGroup({
      start: new FormControl<Date | null>(starttDateOfLastMonth),
      end: new FormControl<Date | null>(today)
    });
  }

  getStartOfLastMonth(): Date {
    const today = new Date();
    // Move to last month
    const lastMonth = today.getMonth() - 1;
    const year = lastMonth < 0 ? today.getFullYear() - 1 : today.getFullYear();
    const month = (lastMonth + 12) % 12; // handle January case
    return new Date(year, month, 1); // first day of last month
  }

  private loadMarkets(): void {
    this._lookupService.getMarketsLookup().pipe(takeUntil(this.destroy$)).subscribe({
      next: (regions: RegionsInfo[]) => {
        this.markets.set(regions);
      }
    })

    let roles = this.rolesService.getRolesAsString();
    this.permissions.getRolePermissionsNew(roles, ScreenName.ToteTransaction, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        this.controlPermissions = perms;
        console.log("tote transaction permissions ", perms);
      }
    })
  }
  // Filtered views (case-insensitive)
  marketsFiltered = computed(() => {
    const q = this.marketQuery().trim().toLowerCase();
    return this.markets().filter(m => m.regionName.toLowerCase().includes(q));
  });

   filteredGenerators = computed(() => {
    const q = this.generatorQuery().trim().toLowerCase();
    return this.generators().filter(m => m.customerName.toLowerCase().includes(q));
  });


  // --- select handlers
  onMarketSelected(value: number): void {
    this.selectedMarketId.set(value);
  }
  onMarketQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.marketQuery.set(searchTerm);
  }
  onMarketOpened(open: boolean): void {
    if (open) setTimeout(() => this.marketFilterInput?.nativeElement.focus(), 0);
  }

  onGeneratorSelected(value: number): void {
    this.selectedGeneratorId = value;
  }

  onGeneratorQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.generatorQuery.set(searchTerm);
  } 

  private loadGenerators(marketId: number): void {
    this._fsService.getToteGenerators(marketId.toString()).pipe(takeUntil(this.destroy$)).subscribe({
      next: (generators: GetMarketGeneratorsResponse[]) => {
        this.generators.set(generators);
      }
    })
  }

  private getToteTransactions(): void {
    this.range.controls['start'].markAsDirty();
    this.range.controls['start'].markAsTouched();
    this.range.controls['end'].markAsDirty();
    this.range.controls['end'].markAsTouched();
    this.range.controls['start'].updateValueAndValidity();
    this.range.controls['end'].updateValueAndValidity();
    this.range.markAllAsTouched();
    this.range.updateValueAndValidity();
        
    if(this.range.invalid){
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    if (this.range.invalid || !this.range.value.start || !this.range.value.end) {
      this._cdr.detectChanges();
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    const fromDate = this.range.value.start ? this.range.value.start.toISOString().slice(0, 10) : '';
    const toDate = this.range.value.end ? this.range.value.end.toISOString().slice(0, 10) : '';
    this._toteTxService.getToteTransactions(this.selectedGeneratorId, fromDate, toDate).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: ToteTransactionRecord[]) => {
        this.toteTransactions.data = [...result];
        this.toteTransactions.sort = this.sort;

        // Manually trigger change detection since we're using OnPush strategy
        this._cdr.markForCheck();
      }
    })
  }

  searchToteTransactions(): void {
    this.getToteTransactions();
  }

  openUpsertPopup(row: ToteTransactionRecord | null, mode: string): void {
    let defaultTx: ToteTransactionInputData = {} as ToteTransactionInputData;

    if (mode == 'Add') {
      defaultTx = {
        id: 0,
        mode: 'Add',
        generators: this.generators(),
        canSave: this.controlPermissions.addButton
      };
    }
    else if (mode == 'Edit') {
      defaultTx = {
        id: row!.id,
        mode: 'Edit',
        generators: this.generators(),
        canSave: this.controlPermissions.edit
      };
    }
    else if (mode == 'Duplicate') {
      defaultTx = {
        id: row!.id,
        mode: 'Duplicate',
        generators: this.generators(),
        canSave: this.controlPermissions.duplicate
      };
    }

    const dialogRef = this._dialog.open(AddEditToteTransactionComponent, {
      data: defaultTx,
      width: '750px',
      maxHeight: '90vh'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.getToteTransactions();
      }
      // if (result) {
      //   const index = this.toteTransactions.data.findIndex(tx => tx.id === row?.id);
      //   if (index == -1) return;

      //  //mutate the existing record
      //   this.toteTransactions.data[index] = { ...this.toteTransactions.data[index], ...result };

      //   //trigger table update with  out replacing array
      //   this.toteTransactions._updateChangeSubscription(); // Refresh the dataSource
      // }
    });
  }

  onDelete(id: number) {
    const dialogData: ConfirmationDialogData = {
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete transaction?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'warn'
    };

    this._dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      panelClass: 'delete-confirmation-dialog',
      data: dialogData,
      disableClose: true
    }).afterClosed().subscribe(result => {
      if (result) {
        this._toteTxService.deleteRecordById(id).subscribe({
          next: () => {
            this.getToteTransactions();
          }
        });
      }
    });
  }

  // --- trackBy helpers
  trackByMarketId = (_: number, item: RegionsInfo) => item.id;
  trackByTransactionId = (_: number, item: ToteTransactionRecord) => item.id;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
    setTimeout(() => {
      this.range.updateValueAndValidity();
      this.range.markAllAsTouched();
    });
  }

  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}
